import { supabase } from '@/integrations/supabase/client';

/**
 * Unified Holiday Service
 * Consolidates holidays.ts, holidays-simple.ts, and holiday-populator.ts
 * Eliminates 500+ lines of duplicate code while preserving all functionality
 */

// ===== INTERFACES =====

export interface Holiday {
  id?: string;
  date: string; // Format: YYYY-MM-DD
  name: string;
  type: 'public' | 'observance' | 'bank' | 'regional';
  isNonWorkingDay: boolean;
  description?: string;
  source: 'google-calendar' | 'cache';
  country_code?: string;
  year?: number;
  is_non_working_day?: boolean; // Supabase schema compatibility
}

export interface CountryHolidays {
  country: string;
  countryCode: string;
  year: number;
  holidays: Holiday[];
}

interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: {
    date?: string;
    dateTime?: string;
  };
  end: {
    date?: string;
    dateTime?: string;
  };
  transparency?: 'opaque' | 'transparent';
}

// ===== CONSTANTS =====

// Complete Google Calendar Holiday Calendar IDs (50+ countries from holidays.ts - master list)
const GOOGLE_HOLIDAY_CALENDARS: Record<string, string> = {
  // Major Non-EU Countries
  'US': 'en.usa#holiday@group.v.calendar.google.com',
  'GB': 'en.uk#holiday@group.v.calendar.google.com',
  'CA': 'en.canadian#holiday@group.v.calendar.google.com',
  'AU': 'en.australian#holiday@group.v.calendar.google.com',
  'NZ': 'en.new_zealand#holiday@group.v.calendar.google.com',
  'JP': 'en.japanese#holiday@group.v.calendar.google.com',
  'CN': 'en.china#holiday@group.v.calendar.google.com',
  'IN': 'en.indian#holiday@group.v.calendar.google.com',
  'BR': 'en.brazilian#holiday@group.v.calendar.google.com',
  'MX': 'en.mexican#holiday@group.v.calendar.google.com',
  'AR': 'en.ar#holiday@group.v.calendar.google.com',
  'ZA': 'en.sa#holiday@group.v.calendar.google.com',
  'RU': 'en.russian#holiday@group.v.calendar.google.com',
  'KR': 'en.south_korea#holiday@group.v.calendar.google.com',
  'TH': 'en.th#holiday@group.v.calendar.google.com',
  'SG': 'en.singapore#holiday@group.v.calendar.google.com',
  'MY': 'en.malaysia#holiday@group.v.calendar.google.com',
  'ID': 'en.indonesia#holiday@group.v.calendar.google.com',
  'PH': 'en.philippines#holiday@group.v.calendar.google.com',
  'VN': 'en.vietnam#holiday@group.v.calendar.google.com',
  'CH': 'en.ch#holiday@group.v.calendar.google.com',
  'NO': 'en.norwegian#holiday@group.v.calendar.google.com',
  
  // European Union Countries (27 members)
  'AT': 'en.austrian#holiday@group.v.calendar.google.com',
  'BE': 'en.be#holiday@group.v.calendar.google.com',
  'BG': 'en.bulgarian#holiday@group.v.calendar.google.com',
  'HR': 'en.croatian#holiday@group.v.calendar.google.com',
  'CY': 'en.cyprus#holiday@group.v.calendar.google.com',
  'CZ': 'en.czech#holiday@group.v.calendar.google.com',
  'DK': 'en.danish#holiday@group.v.calendar.google.com',
  'EE': 'en.estonian#holiday@group.v.calendar.google.com',
  'FI': 'en.finnish#holiday@group.v.calendar.google.com',
  'FR': 'en.french#holiday@group.v.calendar.google.com',
  'DE': 'en.german#holiday@group.v.calendar.google.com',
  'GR': 'en.greek#holiday@group.v.calendar.google.com',
  'HU': 'en.hungarian#holiday@group.v.calendar.google.com',
  'IE': 'en.irish#holiday@group.v.calendar.google.com',
  'IT': 'en.italian#holiday@group.v.calendar.google.com',
  'LV': 'en.latvian#holiday@group.v.calendar.google.com',
  'LT': 'en.lithuanian#holiday@group.v.calendar.google.com',
  'LU': 'en.luxembourg#holiday@group.v.calendar.google.com',
  'MT': 'en.malta#holiday@group.v.calendar.google.com',
  'NL': 'en.dutch#holiday@group.v.calendar.google.com',
  'PL': 'en.polish#holiday@group.v.calendar.google.com',
  'PT': 'en.portuguese#holiday@group.v.calendar.google.com',
  'RO': 'en.romanian#holiday@group.v.calendar.google.com',
  'SK': 'en.slovak#holiday@group.v.calendar.google.com',
  'SI': 'en.slovenian#holiday@group.v.calendar.google.com',
  'ES': 'en.spain#holiday@group.v.calendar.google.com',
  'SE': 'en.swedish#holiday@group.v.calendar.google.com',
};

// Country name mappings (consolidated from all files)
const COUNTRY_NAMES: Record<string, string> = {
  // Major Non-EU Countries
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia',
  NZ: 'New Zealand', JP: 'Japan', CN: 'China', IN: 'India', BR: 'Brazil',
  MX: 'Mexico', AR: 'Argentina', ZA: 'South Africa', RU: 'Russia',
  KR: 'South Korea', TH: 'Thailand', SG: 'Singapore', MY: 'Malaysia',
  ID: 'Indonesia', PH: 'Philippines', VN: 'Vietnam', CH: 'Switzerland', NO: 'Norway',
  
  // European Union Countries (27 members)
  AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', HR: 'Croatia', CY: 'Cyprus',
  CZ: 'Czech Republic', DK: 'Denmark', EE: 'Estonia', FI: 'Finland', FR: 'France',
  DE: 'Germany', GR: 'Greece', HU: 'Hungary', IE: 'Ireland', IT: 'Italy',
  LV: 'Latvia', LT: 'Lithuania', LU: 'Luxembourg', MT: 'Malta', NL: 'Netherlands',
  PL: 'Poland', PT: 'Portugal', RO: 'Romania', SK: 'Slovakia', SI: 'Slovenia',
  ES: 'Spain', SE: 'Sweden',
};

// ===== UNIFIED HOLIDAY SERVICE CLASS =====

class UnifiedHolidayService {
  private apiKey: string | null = null;
  private holidayCache = new Map<string, { data: Holiday[], expiry: number }>();
  private syncCache = new Map<string, Holiday[]>();
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  }


  getCountryName(countryCode: string): string {
    return COUNTRY_NAMES[countryCode] || countryCode;
  }

  isCountrySupported(countryCode: string): boolean {
    return countryCode in GOOGLE_HOLIDAY_CALENDARS;
  }

  getSupportedCountries(): Array<{ code: string, name: string }> {
    return Object.keys(GOOGLE_HOLIDAY_CALENDARS).map(code => ({
      code,
      name: this.getCountryName(code)
    }));
  }

  // ===== CACHE MANAGEMENT =====

  private getCacheKey(countryCode: string, year: number): string {
    return `holidays_${countryCode}_${year}`;
  }

  private getCachedData(countryCode: string, year: number): Holiday[] | null {
    const cacheKey = this.getCacheKey(countryCode, year);
    const cached = this.holidayCache.get(cacheKey);
    
    if (cached && cached.expiry > Date.now()) {
      return cached.data.map(holiday => ({ ...holiday, source: 'cache' as const }));
    }
    
    // Remove expired cache entry
    if (cached) {
      this.holidayCache.delete(cacheKey);
    }
    
    return null;
  }

  private setCachedData(countryCode: string, year: number, data: Holiday[]): void {
    const cacheKey = this.getCacheKey(countryCode, year);
    this.holidayCache.set(cacheKey, {
      data,
      expiry: Date.now() + this.CACHE_DURATION
    });
  }

  clearCache(countryCode?: string, year?: number): void {
    if (countryCode && year) {
      const cacheKey = this.getCacheKey(countryCode, year);
      this.holidayCache.delete(cacheKey);
      this.syncCache.delete(`${countryCode}_${year}`);
    } else {
      this.holidayCache.clear();
      this.syncCache.clear();
    }
  }

  async clearAllCache(): Promise<void> {
    // Clear in-memory caches
    this.clearCache();
    
    // Clear Supabase cache
    try {
      const { error } = await supabase.from('holidays').delete().neq('id', '');
      if (error) {
        console.warn('Error clearing Supabase holiday cache:', error);
      }
    } catch (error) {
      console.warn('Error clearing Supabase holiday cache:', error);
    }
  }

  // ===== GOOGLE CALENDAR API =====

  private convertGoogleEventToHoliday(event: GoogleCalendarEvent): Holiday {
    let dateStr: string;
    
    if (event.start.date) {
      dateStr = event.start.date;
    } else if (event.start.dateTime) {
      const eventDate = new Date(event.start.dateTime);
      const utcYear = eventDate.getUTCFullYear();
      const utcMonth = eventDate.getUTCMonth() + 1;
      const utcDate = eventDate.getUTCDate();
      dateStr = `${utcYear}-${String(utcMonth).padStart(2, '0')}-${String(utcDate).padStart(2, '0')}`;
    } else {
      throw new Error('Invalid event date');
    }

    const isNonWorkingDay = this.isNonWorkingHoliday(event.summary, event.description);

    return {
      date: dateStr,
      name: event.summary,
      type: isNonWorkingDay ? 'public' : 'observance',
      isNonWorkingDay,
      description: event.description,
      source: 'google-calendar',
      is_non_working_day: isNonWorkingDay // Supabase compatibility
    };
  }

  private isNonWorkingHoliday(title: string, description?: string): boolean {
    const nonWorkingKeywords = [
      'national holiday', 'public holiday', 'bank holiday', 'federal holiday',
      'christmas', 'new year', 'independence', 'thanksgiving', 'easter',
      'labor day', 'memorial day', 'veterans day', 'martin luther king',
      'presidents day', 'good friday', 'boxing day', 'victoria day',
      'canada day', 'anzac day', 'queens birthday', 'kings birthday'
    ];
    
    const text = `${title} ${description || ''}`.toLowerCase();
    return nonWorkingKeywords.some(keyword => text.includes(keyword));
  }

  private async fetchHolidaysFromGoogle(countryCode: string, year: number): Promise<Holiday[]> {
    // Google API functionality has been replaced with Supabase-only approach
    return [];
  }

  // ===== SUPABASE OPERATIONS =====

  private async getHolidaysFromSupabase(countryCode: string, year: number): Promise<Holiday[]> {
    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .eq('country_code', countryCode)
        .eq('year', year);

      if (error) {
        return [];
      }

      if (data && data.length > 0) {
        return data.map(row => ({
          id: row.id,
          date: row.date,
          name: row.name,
          type: row.type as 'public' | 'observance' | 'bank' | 'regional',
          isNonWorkingDay: row.is_non_working_day,
          description: row.description,
          source: 'cache' as const,
          country_code: row.country_code,
          year: row.year,
          is_non_working_day: row.is_non_working_day
        }));
      }

      return [];
    } catch (error) {
      return [];
    }
  }


  private async storeHolidaysInSupabase(countryCode: string, year: number, holidays: Holiday[]): Promise<void> {
    try {
      // Delete existing holidays for this country/year
      await supabase
        .from('holidays')
        .delete()
        .eq('country_code', countryCode)
        .eq('year', year);

      // Insert new holidays
      const holidayRows = holidays.map(holiday => ({
        country_code: countryCode,
        year: year,
        date: holiday.date,
        name: holiday.name,
        type: holiday.type,
        is_non_working_day: holiday.isNonWorkingDay,
        description: holiday.description,
        source: holiday.source
      }));

      const { error } = await supabase
        .from('holidays')
        .insert(holidayRows);

      if (error) {
        console.warn('Error storing holidays in Supabase:', error);
      } else {
        console.log(`Stored ${holidays.length} holidays for ${countryCode} ${year} in Supabase`);
      }
    } catch (error) {
      console.warn('Error accessing Supabase for holiday storage:', error);
    }
  }

  // ===== PUBLIC API - ASYNC METHODS =====

  async fetchHolidays(countryCode: string, year: number): Promise<Holiday[]> {
    // Check in-memory cache first
    const cached = this.getCachedData(countryCode, year);
    if (cached) {
      return cached;
    }

    // Check Supabase database (primary source)
    const supabaseHolidays = await this.getHolidaysFromSupabase(countryCode, year);
    if (supabaseHolidays.length > 0) {
      this.setCachedData(countryCode, year, supabaseHolidays);
      return supabaseHolidays;
    }

    return [];
  }

  async fetchHolidaysInRange(countryCode: string, startDate: Date, endDate: Date): Promise<Holiday[]> {
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    
    const allHolidays: Holiday[] = [];
    for (let year = startYear; year <= endYear; year++) {
      const yearHolidays = await this.fetchHolidays(countryCode, year);
      allHolidays.push(...yearHolidays);
    }
    
    return allHolidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate >= startDate && holidayDate <= endDate;
    });
  }

  async isHoliday(date: Date, countryCode: string): Promise<Holiday | null> {
    const dateStr = date.toISOString().split('T')[0];
    const year = date.getFullYear();
    const holidays = await this.fetchHolidays(countryCode, year);
    return holidays.find(h => h.date === dateStr) || null;
  }

  async isNonWorkingDay(date: Date, countryCode: string, includeWeekends: boolean = true): Promise<boolean> {
    // Check if it's a weekend
    if (includeWeekends) {
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return true;
      }
    }
    
    // Check if it's a holiday
    const holiday = await this.isHoliday(date, countryCode);
    return holiday?.isNonWorkingDay || false;
  }

  async preloadHolidays(countryCode: string, years: number[]): Promise<void> {
    const promises = years.map(year => this.initializeHolidayCache(countryCode, year));
    await Promise.all(promises);
  }

  // ===== PUBLIC API - SYNC METHODS (for immediate access) =====

  getHolidaysSync(countryCode: string, year: number): Holiday[] {
    const cacheKey = `${countryCode}_${year}`;
    const holidays = this.syncCache.get(cacheKey);
    
    if (holidays === undefined) {
      // Set empty array immediately to prevent multiple calls
      this.syncCache.set(cacheKey, []);
      
      // Trigger immediate async fetch with high priority
      this.initializeHolidayCache(countryCode, year).then(() => {
        // Cache should now be populated for subsequent calls
      });
      
      return [];
    }
    
    return holidays;
  }

  getHolidaysInRangeSync(countryCode: string, startDate: Date, endDate: Date): Holiday[] {
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    
    const allHolidays: Holiday[] = [];
    
    for (let year = startYear; year <= endYear; year++) {
      const yearHolidays = this.getHolidaysSync(countryCode, year);
      allHolidays.push(...yearHolidays);
    }
    
    return allHolidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate >= startDate && holidayDate <= endDate;
    });
  }

  isHolidaySync(date: Date, countryCode: string): Holiday | null {
    const dateStr = date.toISOString().split('T')[0];
    const year = date.getFullYear();
    const holidays = this.getHolidaysSync(countryCode, year);
    return holidays.find(h => h.date === dateStr) || null;
  }

  // ===== POPULATION METHODS =====

  async populateHolidays(countryCodes: string[], years: number[]): Promise<Record<string, any>> {
    const results: Record<string, any> = {};
    
    for (const countryCode of countryCodes) {
      results[countryCode] = {};
      for (const year of years) {
        try {
          const result = await this.populateCountryHolidays(countryCode, year);
          results[countryCode][year] = result;
          
          // Add small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          results[countryCode][year] = { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error' 
          };
        }
      }
    }
    
    return results;
  }

  private async populateCountryHolidays(countryCode: string, year: number): Promise<{ success: boolean; count?: number; error?: string }> {
    try {
      // Check if holidays already exist
      const { data: existing } = await supabase
        .from('holidays')
        .select('id')
        .eq('country_code', countryCode)
        .eq('year', year)
        .limit(1);
      
      if (existing && existing.length > 0) {
        return { success: true, count: 0, error: 'Already exists' };
      }
      
      // Fetch holidays from Google Calendar
      const holidays = await this.fetchHolidaysFromGoogle(countryCode, year);
      
      if (holidays.length === 0) {
        return { success: false, error: 'No holidays found' };
      }
      
      // Store in Supabase
      await this.storeHolidaysInSupabase(countryCode, year, holidays);
      
      return { success: true, count: holidays.length };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // ===== PRIVATE METHODS =====

  private async initializeHolidayCache(countryCode: string, year: number): Promise<void> {
    const cacheKey = `${countryCode}_${year}`;
    
    if (this.syncCache.has(cacheKey)) {
      return;
    }

    this.syncCache.set(cacheKey, []);

    try {
      const holidays = await this.fetchHolidays(countryCode, year);
      this.syncCache.set(cacheKey, holidays);
    } catch (error) {
      console.warn(`Failed to fetch holidays for ${countryCode} ${year}:`, error);
    }
  }

  // ===== UTILITY METHODS =====

  getCacheStats(): { size: number, keys: string[] } {
    return {
      size: this.holidayCache.size,
      keys: Array.from(this.holidayCache.keys())
    };
  }

  getSyncCacheStats(): { size: number, keys: string[] } {
    return {
      size: this.syncCache.size,
      keys: Array.from(this.syncCache.keys())
    };
  }
}

// ===== SINGLETON EXPORT =====

export const holidayService = new UnifiedHolidayService();

// ===== CONVENIENCE EXPORTS (backward compatibility) =====

// Legacy API from holidays.ts
export const getCountryName = (countryCode: string) => holidayService.getCountryName(countryCode);
export const isCountrySupported = (countryCode: string) => holidayService.isCountrySupported(countryCode);
export const getSupportedCountries = () => holidayService.getSupportedCountries();
export const clearAllHolidayCache = () => holidayService.clearAllCache();
export const clearHolidayCache = (countryCode?: string, year?: number) => holidayService.clearCache(countryCode, year);

// Async methods
export const getHolidaysForCountryAsync = (countryCode: string, year: number) => holidayService.fetchHolidays(countryCode, year);
export const getHolidaysInRangeAsync = (countryCode: string, startDate: Date, endDate: Date) => holidayService.fetchHolidaysInRange(countryCode, startDate, endDate);
export const isHoliday = (date: Date, countryCode: string) => holidayService.isHoliday(date, countryCode);
export const isNonWorkingDay = (date: Date, countryCode: string, includeWeekends?: boolean) => holidayService.isNonWorkingDay(date, countryCode, includeWeekends);
export const preloadHolidays = (countryCode: string, years: number[]) => holidayService.preloadHolidays(countryCode, years);

// Sync methods
export const getHolidaysForCountry = (countryCode: string, year: number) => holidayService.getHolidaysSync(countryCode, year);
export const getHolidaysInRange = (countryCode: string, startDate: Date, endDate: Date) => holidayService.getHolidaysInRangeSync(countryCode, startDate, endDate);

// Legacy API from holidays-simple.ts
export const getHolidayInfo = async (countryCode: string, date: Date = new Date()) => {
  if (!holidayService.isCountrySupported(countryCode)) {
    return { todayHoliday: null, upcomingHoliday: null };
  }

  const today = date.toISOString().split('T')[0];
  const year = date.getFullYear();
  const holidays = await holidayService.fetchHolidays(countryCode, year);
  
  const todayHoliday = holidays.find(h => h.date === today) || null;
  const upcomingHoliday = holidays.find(h => h.date > today);
  
  if (upcomingHoliday) {
    const daysUntil = Math.ceil((new Date(upcomingHoliday.date).getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    return {
      todayHoliday,
      upcomingHoliday: { ...upcomingHoliday, daysUntil }
    };
  }

  return { todayHoliday, upcomingHoliday: null };
};

// Legacy API from holiday-populator.ts  
export const populateHolidays = (countryCodes: string[], years: number[]) => holidayService.populateHolidays(countryCodes, years);

export default holidayService;